(() => {
  'use strict';
  if (customElements.get('ho-product-description')) return;
  const clean = value => String(value || '').replace(/\s+/g, ' ').trim();
  const heading = node => /^H[2-6]$/.test(node.nodeName);
  const notice = /^(?:please note|disclaimer|important(?: information| warnings?)?|warning|caution)\s*:/i;
  const labels = {where: 'Where it works best', care: 'Planting and care', supply: 'Sizes and what you receive', delivery: 'Delivery and unpacking'};
  function groupFor(value) {
    const text = clean(value).toLowerCase();
    if (/disclaimer|important|warning|please note|about the image|plant selection|substitution/.test(text)) return 'warnings';
    if (/unit size|choosing a height|root types?|planting densit|minimum order|what you (?:will )?receive|supplied|specification|how to order/.test(text)) return 'supply';
    if (/delivery|unpacking/.test(text)) return 'delivery';
    if (/landscap|design appeal|where .*works|where to plant/.test(text)) return 'where';
    if (/growing conditions|preferred conditions|maintenance|\bcare\b|\bprun(?:ing|e)\b|watering|planting/.test(text)) return 'care';
    return 'about';
  }
  function rawHeading(value) {
    const text = clean(value), words = text.split(' ');
    return words.length < 15 && !/[.,;]$/.test(text) && (text.endsWith('?') || words.filter(word => /^[A-Z]/.test(word)).length > words.length / 2);
  }
  function blocks(parent) {
    const result = [];
    let paragraph = null;
    const flush = () => { if (paragraph && (clean(paragraph.textContent) || paragraph.querySelector('img,svg,video,iframe'))) result.push(paragraph); paragraph = null; };
    const append = node => { if (!paragraph) paragraph = document.createElement('p'); paragraph.append(node); };
    Array.from(parent.childNodes).forEach(node => {
      if (node.nodeType === 3) {
        const pieces = node.textContent.replace(/\r\n?|\n\r/g, '\n').split(/\n\s*\n/);
        pieces.forEach((piece, index) => {
          if (index) flush();
          if (!paragraph && rawHeading(piece) && (pieces.length > 1 || /^\s*\n/.test(node.textContent))) {
            const title = document.createElement('h3'); title.textContent = piece; result.push(title);
          } else append(document.createTextNode(piece));
        });
      } else if (node.nodeType === 1) {
        const standaloneTitle = /^(STRONG|B)$/.test(node.nodeName) && !paragraph && clean(node.textContent).split(' ').length < 15 && /^\s*\n/.test(node.nextSibling?.textContent || '');
        if (heading(node) || standaloneTitle) {
          flush();
          const title = document.createElement('h3');
          if (standaloneTitle) title.append(node); else title.append(...Array.from(node.childNodes));
          result.push(title);
        } else if (/^(P|UL|OL|TABLE|BLOCKQUOTE|FIGURE|PRE|HR|DIV|SECTION)$/.test(node.nodeName)) {
          flush();
          if (node.nodeName === 'DIV' && node.classList.contains('metafield-rich_text_field')) result.push(...blocks(node));
          else result.push(node);
        } else append(node);
      }
    });
    flush();
    return result;
  }
  // Split only a prose block at an exact text offset; preserve links and emphasis on either side.
  function splitAt(node, offset) {
    const before = node.cloneNode(false), after = node.cloneNode(false);
    let remaining = offset;
    Array.from(node.childNodes).forEach(child => {
      const length = child.textContent.length;
      if (remaining >= length) { before.append(child); remaining -= length; }
      else if (remaining <= 0) after.append(child);
      else if (child.nodeType === 3) {
        before.append(document.createTextNode(child.textContent.slice(0, remaining)));
        after.append(document.createTextNode(child.textContent.slice(remaining)));
        remaining = 0;
      } else {
        const halves = splitAt(child, remaining);
        before.append(halves[0]); after.append(halves[1]); remaining = 0;
      }
    });
    return [before, after];
  }
  function introEnd(text) {
    if (clean(text).split(' ').length <= 65) return text.length;
    const sentences = typeof Intl.Segmenter === 'function'
      ? Array.from(new Intl.Segmenter('en', {granularity: 'sentence'}).segment(text), item => item.segment)
      : text.match(/[^.!?]+[.!?]+(?:\s+|$)|[^.!?]+$/g) || [text];
    let end = 0;
    for (const sentence of sentences.slice(0, 2)) {
      if (end && clean(text.slice(0, end + sentence.length)).split(' ').length > 65) break;
      end += sentence.length;
    }
    return end || text.length;
  }
  function meaningful(node, group) {
    const text = clean(node.textContent);
    const words = text.split(' ').length;
    return node.nodeName === 'P' && (group === 'about' || group === 'where') && !notice.test(text) && !/^(?:supplied details|delivery method|unit size)\s*:/i.test(text) && (words >= 12 || words >= 6 && /[.!?]$/.test(text));
  }
  function paragraphKey(node) {
    return node.nodeName === 'P' ? node.innerHTML.replace(/\s+/g, ' ').trim() : '';
  }
  function safeIntroEnd(node, offset) {
    let position = 0;
    for (const child of node.childNodes) {
      const end = position + child.textContent.length;
      if (child.nodeType === 1 && offset > position && offset < end) return end;
      position = end;
    }
    return offset;
  }
  class ProductDescription extends HTMLElement {
    connectedCallback() {
      this.onOverviewClick ||= event => {
        const link = event.target.closest('[data-ho-description-link]');
        if (!link || link.getAttribute('aria-controls') !== this.id) return;
        event.preventDefault();
        this.reveal();
      };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => this.enhance(), {once: true});
      else this.enhance();
    }
    disconnectedCallback() {
      this.overview?.removeEventListener('click', this.onOverviewClick);
    }
    reveal() {
      let ancestor = this.parentElement?.closest('details');
      while (ancestor) {
        ancestor.open = true;
        ancestor = ancestor.parentElement?.closest('details');
      }
      const details = this.querySelector('[data-ho-description-shell]') || this.querySelector('[data-ho-description-about], .ho-product-description__item');
      if (details && details.tagName === 'DETAILS') details.open = true;
      requestAnimationFrame(() => {
        const header = document.querySelector('[data-site-header]');
        this.style.scrollMarginTop = Math.ceil((header?.getBoundingClientRect().height || 0) + 16) + 'px';
        details?.querySelector('summary')?.focus({preventScroll: true});
        this.scrollIntoView({behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start'});
      });
    }
    enhance() {
      if (!this.isConnected) return;
      const overview = document.getElementById(this.dataset.overviewId);
      if (this.overview !== overview) this.overview?.removeEventListener('click', this.onOverviewClick);
      this.overview = overview;
      overview?.addEventListener('click', this.onOverviewClick);
      if (this.dataset.enhanced) return;
      const source = this.querySelector('[data-ho-description-source]');
      const singleCollapse = this.hasAttribute('data-single-collapse');
      const explicitOverview = this.hasAttribute('data-explicit-overview') || singleCollapse;
      const panels = singleCollapse ? this.querySelector('[data-ho-description-panels]') || this : this;
      const groups = {about: [], where: [], care: [], supply: [], delivery: [], warnings: []};
      let intro = null, group = 'about';
      try {
        const original = source ? blocks(source.cloneNode(true)) : [];
        original.forEach(node => {
          if (heading(node)) {
            if (this.dataset.productTitle && clean(node.textContent).toLowerCase() === clean(this.dataset.productTitle).toLowerCase()) { group = 'about'; return; }
            group = groupFor(node.textContent); groups[group].push(node); return;
          }
          if (node.nodeName === 'P') {
            const match = /(?:^|\n|[.!?]\s+)(\s*(?:Please note|Disclaimer|Important information|Warning|Caution)\s*:)/i.exec(node.textContent);
            if (match && clean(node.textContent.slice(0, match.index))) {
              const offset = match.index + match[0].indexOf(match[1]);
              const parts = splitAt(node, offset);
              node = parts[0]; groups.warnings.push(parts[1]);
            } else if (notice.test(clean(node.textContent))) { groups.warnings.push(node); return; }
          }
          if (!explicitOverview && !intro && overview && meaningful(node, group)) {
            const end = safeIntroEnd(node, introEnd(node.textContent));
            if (end < node.textContent.length) {
              const parts = splitAt(node, end); intro = parts[0];
              if (clean(parts[1].textContent)) groups[group].push(parts[1]);
            } else intro = node;
          } else groups[group].push(node);
        });
        const seen = new Set([intro, ...Object.values(groups).flat()].filter(Boolean).map(paragraphKey).filter(Boolean));
        const existing = new Map();
        this.querySelectorAll('[data-ho-description-group], [data-ho-description-warnings]').forEach(item => {
          const key = item.hasAttribute('data-ho-description-warnings') ? 'warnings' : item.dataset.hoDescriptionGroup;
          if (!(key in groups)) return;
          existing.set(key, item);
          const content = item.querySelector('[data-ho-description-content]');
          if (!content) return;
          blocks(content.cloneNode(true)).forEach(node => {
            const signature = paragraphKey(node);
            if (signature && seen.has(signature)) return;
            groups[key].push(node); if (signature) seen.add(signature);
          });
        });
        const output = document.createDocumentFragment();
        let opened = false;
        Object.entries(groups).forEach(([key, nodes]) => {
          if (!nodes.some(node => clean(node.textContent) || node.querySelector?.('img,svg,video,iframe,hr'))) return;
          if (nodes.every(heading)) {
            const remaining = nodes.filter(node => !/^(?:summary|overview|introduction|description|about (?:this )?(?:plant|product))$/i.test(clean(node.textContent)));
            if (remaining.length) {
              const standalone = document.createElement('div'); standalone.className = 'ho-product-description__content rte'; standalone.setAttribute('data-ho-description-content', ''); standalone.append(...remaining); output.append(standalone);
            }
            return;
          }
          const warning = key === 'warnings';
          const item = document.createElement(warning || singleCollapse ? 'section' : 'details');
          item.className = warning ? 'ho-product-description__warnings' : singleCollapse ? 'ho-product-description__group' : 'ho-product-description__item';
          item.setAttribute(warning ? 'data-ho-description-warnings' : key === 'about' ? 'data-ho-description-about' : 'data-ho-description-group', key === 'about' || warning ? '' : key);
          const title = document.createElement(warning || singleCollapse ? 'h3' : 'summary');
          title.textContent = existing.get(key)?.querySelector(warning ? 'h3' : 'summary')?.textContent || (warning ? 'Important information' : key === 'about' ? this.dataset.aboutLabel || 'About this product' : labels[key]);
          if (!warning && !opened && this.dataset.fullLabel) title.textContent = this.dataset.fullLabel;
          const content = document.createElement('div'); content.className = 'ho-product-description__content rte'; content.setAttribute('data-ho-description-content', ''); content.append(...nodes);
          item.append(title, content);
          if (!warning && !opened) { if (!singleCollapse) { item.open = true; item.setAttribute('open', ''); } opened = true; }
          output.append(item);
        });
        panels.replaceChildren(output);
        if (overview && !explicitOverview) { if (intro) (overview.querySelector('[data-ho-description-summary]') || overview).replaceChildren(intro); else overview.hidden = true; }
        const link = overview?.querySelector('[data-ho-description-link]');
        if (link) link.hidden = !panels.children.length;
        this.dataset.enhanced = 'true';
      } catch (_) { /* Leave the complete original description visible if enhancement cannot finish. */ }
    }
  }
  customElements.define('ho-product-description', ProductDescription);
})();



